import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, Eye, Filter, Users as UsersIcon, User, Car } from 'lucide-react';
import { getAllUsers } from '../../services/admin/userService';
import { toast } from '../../hooks/use-toast';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: 'all',
    verification_status: 'all',
    is_suspended: 'all'
  });
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      loadUsers();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, filters]);

  const loadUsers = async () => {
    setLoading(true);
    const result = await getAllUsers({ limit: 1000 });
    
    if (result.success) {
      setUsers(result.data.users);
      setLastRefresh(new Date());
      // Don't show toast on auto-refresh, only on manual
      if (!autoRefresh || users.length === 0) {
        toast({ title: 'Users loaded', description: `Found ${result.data.users.length} users` });
      }
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    
    setLoading(false);
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filter
    if (filters.role !== 'all') {
      filtered = filtered.filter(user => user.role === filters.role);
    }

    // Verification filter
    if (filters.verification_status !== 'all') {
      filtered = filtered.filter(user => user.verification_status === filters.verification_status);
    }

    // Suspension filter
    if (filters.is_suspended !== 'all') {
      const isSuspended = filters.is_suspended === 'true';
      filtered = filtered.filter(user => user.is_suspended === isSuspended);
    }

    setFilteredUsers(filtered);
  };

  const getVerificationBadge = (status) => {
    const badges = {
      pending: <Badge className="bg-yellow-500">Pending</Badge>,
      in_progress: <Badge className="bg-blue-500">In Progress</Badge>,
      verified: <Badge className="bg-green-500">Verified</Badge>,
      rejected: <Badge className="bg-red-500">Rejected</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">User Management</h2>
          <p className="text-gray-600">
            Manage riders and drivers
            {autoRefresh && (
              <span className="ml-2 text-xs text-green-600">
                • Auto-refresh (last: {lastRefresh.toLocaleTimeString()})
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-gray-600">Auto-refresh (30s)</span>
          </label>
          <Button 
            onClick={loadUsers} 
            className="bg-green-600 hover:bg-green-700"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <UsersIcon className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold">{users.length}</p>
              <p className="text-sm text-gray-600">Total Users</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <User className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-2xl font-bold">{users.filter(u => u.role === 'rider').length}</p>
              <p className="text-sm text-gray-600">Riders</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Car className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-2xl font-bold">{users.filter(u => u.role === 'driver').length}</p>
              <p className="text-sm text-gray-600">Drivers</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Filter className="w-8 h-8 text-orange-600" />
            <div>
              <p className="text-2xl font-bold">{filteredUsers.length}</p>
              <p className="text-sm text-gray-600">Filtered</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Name, phone, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Role</label>
            <select
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Roles</option>
              <option value="rider">Riders</option>
              <option value="driver">Drivers</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Verification</label>
            <select
              value={filters.verification_status}
              onChange={(e) => setFilters({ ...filters, verification_status: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Suspension</label>
            <select
              value={filters.is_suspended}
              onChange={(e) => setFilters({ ...filters, is_suspended: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All</option>
              <option value="false">Active</option>
              <option value="true">Suspended</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Users Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verification</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="font-medium text-gray-900">
                        {user.first_name} {user.middle_name} {user.last_name}
                      </div>
                      {user.email && (
                        <div className="text-sm text-gray-500">{user.email}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge className={user.role === 'driver' ? 'bg-purple-500' : 'bg-blue-500'}>
                      {user.role}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.country_code} {user.phone}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getVerificationBadge(user.verification_status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.is_suspended ? (
                      <Badge className="bg-red-500">Suspended</Badge>
                    ) : (
                      <Badge className="bg-green-500">Active</Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                      className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No users found matching your filters</p>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
